import React from "react";
import Skeleton from "react-loading-skeleton";

// --- ViewSwitcher Component ---
interface ViewSwitcherProps {
  activeView: "grid" | "list";
  onViewChange: (view: "grid" | "list") => void;
}

const GridIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0 0H7V7H0V0ZM9 0H16V7H9V0ZM0 9H7V16H0V9ZM9 9H16V16H9V9Z" />
  </svg>
);

const ListIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0 0H16V3H0V0ZM0 6.5H16V9.5H0V6.5ZM0 13H16V16H0V13Z" />
  </svg>
);

const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  activeView,
  onViewChange,
}) => {
  return (
    <div className="view-switcher">
      <button
        type="button"
        className={`view-switcher-button ${
          activeView === "grid" ? "active" : ""
        }`}
        onClick={() => onViewChange("grid")}
        aria-pressed={activeView === "grid"}
        aria-label="Grid View"
      >
        <GridIcon />
      </button>
      <button
        type="button"
        className={`view-switcher-button ${
          activeView === "list" ? "active" : ""
        }`}
        onClick={() => onViewChange("list")}
        aria-pressed={activeView === "list"}
        aria-label="List View"
      >
        <ListIcon />
      </button>
    </div>
  );
};

// --- PerPageSelector Component ---
interface PerPageSelectorProps {
  currentPerPage: number;
  onPerPageChange: (perPage: number) => void;
  options?: number[];
}

const PerPageSelector: React.FC<PerPageSelectorProps> = ({
  currentPerPage,
  onPerPageChange,
  options = [4, 8, 12], // Default options
}) => {
  return (
    <div className="per-page-selector">
      <span className="per-page-selector-label">Göster:</span>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`per-page-selector-button ${
            currentPerPage === option ? "active" : ""
          }`}
          onClick={() => onPerPageChange(option)}
          aria-pressed={currentPerPage === option}
        >
          {option}
        </button>
      ))}
    </div>
  );
};

// --- SearchBar Component ---
interface SearchBarProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  onKeyDown,
  onClearSearch,
  placeholder,
}) => {
  return (
    <div className="search-bar-input-group">
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={onSearchChange}
        onKeyDown={onKeyDown}
        className="search-bar-input"
      />
      {searchTerm && (
        <button
          type="button"
          className="search-bar-clear-button"
          onClick={onClearSearch}
          aria-label="Clear search"
        >
          &times; {/* Simple "x" character */}
        </button>
      )}
      <button
        onClick={onSearchSubmit}
        className="search-bar-button"
        type="button"
      >
        Ara
      </button>
    </div>
  );
};

// --- ProductCountDisplay Component ---
interface ProductCountDisplayProps {
  count: number;
}

const ProductCountDisplay: React.FC<ProductCountDisplayProps> = ({ count }) => {
  return <span className="product-count-display">Toplam : {count} Ürün</span>;
};

// --- ProductsTopBar Component ---
interface ProductsTopBarProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  placeholder?: string;
  activeView: "grid" | "list";
  onViewChange: (view: "grid" | "list") => void;
  perPage: number;
  onPerPageChange: (perPage: number) => void;
  totalProducts: number;
  isLoading: boolean;
  isFetching: boolean;
  error?: any;
}

const ProductsTopBar: React.FC<ProductsTopBarProps> = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  onKeyDown,
  onClearSearch,
  placeholder = "Ürünlerde ara...",
  activeView,
  onViewChange,
  perPage,
  onPerPageChange,
  totalProducts,
  isLoading,
  isFetching,
  error,
}) => {
  if (error) {
    return null; // Fail silently on error
  }

  if (isLoading) {
    return (
      <div className="products-top-bar-skeleton">
        <div className="search-bar-skeleton">
          <Skeleton height={40} width={300} />
        </div>
      </div>
    );
  }

  const topBarClassName = `products-top-bar ${
    isFetching ? "is-fetching" : ""
  }`.trim();

  return (
    <div className={topBarClassName}>
      {isFetching && <div className="products-top-bar-overlay"></div>}
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        onKeyDown={onKeyDown}
        onClearSearch={onClearSearch} // Pass down
        placeholder={placeholder}
      />
      <div className="products-top-bar-controls">
        <ProductCountDisplay count={totalProducts} />
        <PerPageSelector
          currentPerPage={perPage}
          onPerPageChange={onPerPageChange}
        />
        <ViewSwitcher activeView={activeView} onViewChange={onViewChange} />
        {/* Sorting options can be added here later */}
      </div>
    </div>
  );
};

export default ProductsTopBar;
